import type { MarginaliaDatabase } from "@/storage";

import {
  commitDocumentImport,
  parseDocumentImportValue,
  previewDocumentImport,
  type CommitDocumentImportOptions,
  type DocumentImportCommitResult,
  type DocumentImportPreview,
} from "./import-document";
import {
  commitLibraryImport,
  parseLibraryImportValue,
  previewLibraryImport,
  type CommitLibraryImportOptions,
  type LibraryImportCommitResult,
  type LibraryImportPreview,
} from "./import-library";
import { readJsonImportValue, type ReadJsonImportOptions } from "./read-import";

export type MarginaliaBackupImportPreview =
  | {
      kind: "document";
      preview: DocumentImportPreview;
    }
  | {
      kind: "library";
      preview: LibraryImportPreview;
    };

export type MarginaliaBackupImportCommitResult =
  | {
      kind: "document";
      result: DocumentImportCommitResult;
    }
  | {
      kind: "library";
      result: LibraryImportCommitResult;
    };

export type ImportMarginaliaBackupOptions = ReadJsonImportOptions &
  CommitDocumentImportOptions &
  CommitLibraryImportOptions & {
    confirm?: (
      preview: MarginaliaBackupImportPreview,
    ) => boolean | Promise<boolean>;
  };

export type ImportMarginaliaBackupResult =
  | {
      status: "imported";
      preview: MarginaliaBackupImportPreview;
      result: MarginaliaBackupImportCommitResult;
    }
  | {
      status: "cancelled";
      preview: MarginaliaBackupImportPreview;
    };

export async function importMarginaliaBackup(
  db: MarginaliaDatabase,
  input: Blob | string | unknown,
  options: ImportMarginaliaBackupOptions = {},
): Promise<ImportMarginaliaBackupResult> {
  const value = await readJsonImportValue(input, options);
  const format =
    typeof value === "object" && value !== null && "format" in value
      ? (value as { format?: unknown }).format
      : null;

  if (format === "marginalia-library") {
    const library = parseLibraryImportValue(value);
    const libraryPreview = await previewLibraryImport(db, library, options);
    const preview: MarginaliaBackupImportPreview = {
      kind: "library",
      preview: libraryPreview,
    };
    if (options.confirm && !(await options.confirm(preview))) {
      return { status: "cancelled", preview };
    }
    const result = await commitLibraryImport(db, library, options);
    return {
      status: "imported",
      preview,
      result: { kind: "library", result },
    };
  }

  const bundle = parseDocumentImportValue(value);
  const documentPreview = await previewDocumentImport(db, bundle, options);
  const preview: MarginaliaBackupImportPreview = {
    kind: "document",
    preview: documentPreview,
  };
  if (options.confirm && !(await options.confirm(preview))) {
    return { status: "cancelled", preview };
  }
  const result = await commitDocumentImport(db, bundle, options);
  return {
    status: "imported",
    preview,
    result: { kind: "document", result },
  };
}
